import React from 'react';

import { Building2, Clock, RefreshCw, Settings, User } from 'lucide-react';

import { formatRelativeTime, formatTimestamp } from '../../utils/utils';

import { PILL_NEUTRAL, PILL_PRIMARY } from './v9';

interface RecalculationMetadata {
	triggered_by_user_id?: string;
	triggered_by_user_name?: string;
	triggered_at?: string;
	fee_config_id?: string;
	fee_config_name?: string;
	config_name?: string; // Alternative field name
	rate_plan_type?: string;
	skip_cache?: boolean;
	invalidate_cache?: boolean;
	event_type?: string; // 'rerun' or 'recalculation'
}

interface ProcessingLog {
	id: string;
	step_name: string;
	status: string;
	message?: string;
	created_at: string;
	completed_at?: string;
	duration_seconds?: number;
	step_data?: {
		recalculation_metadata?: RecalculationMetadata;
		config_name?: string;
		custom_rates_id?: string;
		rates_source?: string;
		event_type?: string;
		triggered_at?: string;
		triggered_by_user_name?: string;
		triggered_by_user_id?: string;
		fee_config_id?: string;
		fee_config_name?: string;
	};
}

interface RecalculationInfoProps {
	logsData?: {
		logs: ProcessingLog[];
	};
	className?: string;
	customRates?: any; // Custom rates object with name field
	analysisType?: string; // Analysis type from the document (e.g., "cost_comparison")
	actionType?: string; // Action type (e.g., "upload", "rerun", "recalculation")
}

/**
 * Component to display fee configuration name and recalculation history
 * from processing logs step_data
 */
const RecalculationInfo: React.FC<RecalculationInfoProps> = ({
	logsData,
	customRates,
	analysisType,
	actionType,
	className = '',
}) => {
	// Extract the most recent recalculation or rerun metadata from logs
	const getLatestRecalculation = (): RecalculationMetadata | null => {
		if (!logsData?.logs) return null;

		// Look for recalculation or rerun steps in reverse chronological order
		const recalculationLogs = logsData.logs
			.filter(
				(log) =>
					log.step_name?.toLowerCase().includes('recalculation') ||
					log.step_name?.toLowerCase().includes('rerun') ||
					log.step_data?.recalculation_metadata ||
					log.step_data?.config_name ||
					log.step_data?.event_type === 'rerun',
			)
			.sort(
				(a, b) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
			);

		if (recalculationLogs.length === 0) return null;

		const latestLog = recalculationLogs[0];
		const metadata =
			latestLog.step_data?.recalculation_metadata || latestLog.step_data;

		// If we have full metadata, return it
		if (metadata) {
			return {
				...metadata,
				triggered_at: metadata.triggered_at || latestLog.created_at,
				event_type:
					metadata.event_type ||
					(latestLog.step_name?.toLowerCase().includes('rerun')
						? 'rerun'
						: 'recalculation'),
			};
		}

		// Otherwise, construct from available step_data
		return {
			fee_config_name: latestLog.step_data?.config_name,
			config_name: latestLog.step_data?.config_name,
			triggered_at: latestLog.created_at,
			event_type: latestLog.step_name?.toLowerCase().includes('rerun')
				? 'rerun'
				: 'recalculation',
		};
	};

	// Use config name from customRates object (from processing_metadata)
	// This is updated on-the-fly in the detailed API to show current name
	const feeConfigName = customRates?.name;

	// Get all recalculation history
	const getRecalculationHistory = (): RecalculationMetadata[] => {
		if (!logsData?.logs) return [];

		return logsData.logs
			.filter(
				(log) =>
					log.step_name?.toLowerCase().includes('recalculation') &&
					(log.step_data?.recalculation_metadata || log.step_data?.config_name),
			)
			.sort(
				(a, b) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
			)
			.map((log) => {
				const metadata = log.step_data?.recalculation_metadata;
				if (metadata) return metadata;

				return {
					fee_config_name: log.step_data?.config_name,
					config_name: log.step_data?.config_name,
					triggered_at: log.created_at,
				};
			});
	};

	const latestRecalculation = getLatestRecalculation();
	const recalculationHistory = getRecalculationHistory();

	if (!feeConfigName && !latestRecalculation) {
		return null;
	}

	return (
		<div className={`flex items-center gap-2 ${className}`}>
			{/* Fee Config Name Badge */}
			{feeConfigName && (
				<div className="relative group">
					<span className={PILL_NEUTRAL}>
						<Building2 />
						{feeConfigName}
					</span>
					<div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
						Fee configuration used for this analysis
					</div>
				</div>
			)}

			{/* Latest Recalculation/Rerun Info - Show if action_type is "recalculation" or "rerun" */}
			{(actionType === 'recalculation' || actionType === 'rerun') &&
				latestRecalculation &&
				latestRecalculation.triggered_at && (
					<div className="relative group">
						<span className={PILL_PRIMARY}>
							<RefreshCw />
							<span>
								{latestRecalculation.event_type === 'rerun'
									? 'Rerun'
									: 'Recalculated'}{' '}
								{formatRelativeTime(latestRecalculation.triggered_at)}
							</span>
						</span>
						<div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-xs -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
							<div className="space-y-1">
								<div className="font-semibold">
									{latestRecalculation.event_type === 'rerun'
										? 'Last Rerun'
										: 'Last Recalculation'}
								</div>
								<div className="text-xs">
									{formatTimestamp(latestRecalculation.triggered_at)}
								</div>
								{latestRecalculation.triggered_by_user_name && (
									<div className="text-xs">
										By: {latestRecalculation.triggered_by_user_name}
									</div>
								)}
								{(latestRecalculation.fee_config_name ||
									latestRecalculation.config_name) && (
									<div className="text-xs">
										Config:{' '}
										{latestRecalculation.fee_config_name ||
											latestRecalculation.config_name}
									</div>
								)}
							</div>
						</div>
					</div>
				)}

			{/* Recalculation History Count - Show if action_type is "recalculation" or "rerun" */}
			{(actionType === 'recalculation' || actionType === 'rerun') &&
				recalculationHistory.length > 1 && (
					<div className="relative group">
						<span className={`${PILL_NEUTRAL} cursor-pointer`}>
							<Clock />
							<span>{recalculationHistory.length} recalculations</span>
						</span>
						<div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-xs -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
							<div className="space-y-2 max-w-xs">
								<div className="font-semibold text-sm">
									Recalculation History
								</div>
								<div className="space-y-1.5 max-h-48 overflow-y-auto">
									{recalculationHistory.map((recalc, index) => (
										<div
											key={index}
											className="text-xs p-2 bg-gray-700 rounded border border-gray-600"
										>
											<div className="flex items-center gap-1 mb-1">
												<Clock className="h-3 w-3" />
												<span>
													{recalc.triggered_at
														? formatTimestamp(recalc.triggered_at)
														: 'Unknown time'}
												</span>
											</div>
											{recalc.triggered_by_user_name && (
												<div className="flex items-center gap-1 text-gray-300">
													<User className="h-3 w-3" />
													<span>{recalc.triggered_by_user_name}</span>
												</div>
											)}
											{(recalc.fee_config_name || recalc.config_name) && (
												<div className="flex items-center gap-1 text-gray-300">
													<Settings className="h-3 w-3" />
													<span>
														{recalc.fee_config_name || recalc.config_name}
													</span>
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				)}
		</div>
	);
};

export default RecalculationInfo;
